import * as React from "react";
import { Select } from "@base-ui/react/select";
import { cn, type WithStringClassName } from "./cn";

/**
 * v3 Select surface, rendered with CSS classes from menu.css (imported via
 * @sproutsocial/seeds-react-menu/base/css) instead of styled-components. Each
 * export keeps its previous name and forwards props/refs to the underlying
 * Base UI component; the transient `$isInvalid` / `$fullWidth` props are
 * translated to modifier classes here so call sites are unchanged.
 */

type FieldProps = WithStringClassName<React.ComponentProps<"div">> & {
  $fullWidth?: boolean;
};

export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ $fullWidth, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "seeds-menu-field",
        { "seeds-menu-field--inline": $fullWidth === false },
        className
      )}
      {...rest}
    />
  )
);
Field.displayName = "Field";

export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof Select.Label>,
  WithStringClassName<React.ComponentProps<typeof Select.Label>>
>(({ className, ...rest }, ref) => (
  <Select.Label
    ref={ref}
    className={cn("seeds-menu-label", className)}
    {...rest}
  />
));
SelectLabel.displayName = "SelectLabel";

type SelectTriggerProps = WithStringClassName<
  React.ComponentProps<typeof Select.Trigger>
> & {
  $isInvalid?: boolean;
};

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof Select.Trigger>,
  SelectTriggerProps
>(({ $isInvalid, className, ...rest }, ref) => (
  <Select.Trigger
    ref={ref}
    className={cn(
      "seeds-select-trigger",
      { "seeds-select-trigger--invalid": !!$isInvalid },
      className
    )}
    {...rest}
  />
));
SelectTrigger.displayName = "SelectTrigger";

export const SelectValue = React.forwardRef<
  React.ElementRef<typeof Select.Value>,
  WithStringClassName<React.ComponentProps<typeof Select.Value>>
>(({ className, ...rest }, ref) => (
  <Select.Value
    ref={ref}
    className={cn("seeds-select-value", className)}
    {...rest}
  />
));
SelectValue.displayName = "SelectValue";

export const SelectIcon = React.forwardRef<
  React.ElementRef<typeof Select.Icon>,
  WithStringClassName<React.ComponentProps<typeof Select.Icon>>
>(({ className, ...rest }, ref) => (
  <Select.Icon
    ref={ref}
    className={cn("seeds-select-icon", className)}
    {...rest}
  />
));
SelectIcon.displayName = "SelectIcon";

export const SelectGroup = React.forwardRef<
  React.ElementRef<typeof Select.Group>,
  WithStringClassName<React.ComponentProps<typeof Select.Group>>
>(({ className, ...rest }, ref) => (
  <Select.Group
    ref={ref}
    className={cn("seeds-select-group", className)}
    {...rest}
  />
));
SelectGroup.displayName = "SelectGroup";

export const SelectGroupLabel = React.forwardRef<
  React.ElementRef<typeof Select.GroupLabel>,
  WithStringClassName<React.ComponentProps<typeof Select.GroupLabel>>
>(({ className, ...rest }, ref) => (
  <Select.GroupLabel
    ref={ref}
    className={cn("seeds-select-group-label", className)}
    {...rest}
  />
));
SelectGroupLabel.displayName = "SelectGroupLabel";

export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof Select.Separator>,
  WithStringClassName<React.ComponentProps<typeof Select.Separator>>
>(({ className, ...rest }, ref) => (
  <Select.Separator
    ref={ref}
    className={cn("seeds-select-separator", className)}
    {...rest}
  />
));
SelectSeparator.displayName = "SelectSeparator";

export const SelectPositioner = React.forwardRef<
  React.ElementRef<typeof Select.Positioner>,
  WithStringClassName<React.ComponentProps<typeof Select.Positioner>>
>(({ className, ...rest }, ref) => (
  <Select.Positioner
    ref={ref}
    className={cn("seeds-select-positioner", className)}
    {...rest}
  />
));
SelectPositioner.displayName = "SelectPositioner";

export const SelectPopup = React.forwardRef<
  React.ElementRef<typeof Select.Popup>,
  WithStringClassName<React.ComponentProps<typeof Select.Popup>>
>(({ className, ...rest }, ref) => (
  <Select.Popup
    ref={ref}
    className={cn("seeds-select-popup", className)}
    {...rest}
  />
));
SelectPopup.displayName = "SelectPopup";
